import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const FoundationSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1); // 1 = Next, -1 = Prev

  //Unique Slides
  const slides = [
    {
      leftImage: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=600&q=80",
      leftText: "STRONG\nFOUNDATION",
      rightImage: "https://images.unsplash.com/photo-1590486803833-1c5dc8ce34f6?w=800&q=80",
      rightText: "SOLID\nBASE"
    },
    {
      leftImage: "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=600&q=80",
      leftText: "COZY\nBEDROOM",
      rightImage: "https://images.unsplash.com/photo-1595853035070-59a39fe84de3?w=800&q=80",
      rightText: "PEACEFUL\nCOMFORT"
    },
    {
      leftImage: "https://images.unsplash.com/photo-1616486338812-3aeee0770398?w=600&q=80",
      leftText: "STYLISH\nLIVING",
      rightImage: "https://images.unsplash.com/photo-1616137466211-f939a420be84?w=800&q=80",
      rightText: "MODERN\nLIFESTYLE"
    },
    {
      leftImage: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&q=80",
      leftText: "COMMERCIAL\nSPACES",
      rightImage: "https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800&q=80",
      rightText: "PROFESSIONAL\nDESIGN"
    },
    {
      leftImage: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&q=80",
      leftText: "FINAL\nTOUCHES",
      rightImage: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80",
      rightText: "DREAM\nHOME"
    }
  ];

  // Determine indices without wrapping (null if out of bounds)
  const nextIndex = currentIndex < slides.length - 1 ? currentIndex + 1 : null;
  const prevIndex = currentIndex > 0 ? currentIndex - 1 : null;

  const handleNext = () => {
    if (nextIndex !== null) {
      setDirection(1);
      setCurrentIndex(nextIndex);
    }
  };

  const handlePrev = () => {
    if (prevIndex !== null) {
      setDirection(-1);
      setCurrentIndex(prevIndex);
    }
  };

  useEffect(() => {
    // Only auto-advance if not at the end
    if (nextIndex !== null) {
      const interval = setInterval(() => {
        handleNext();
      }, 6000);
      return () => clearInterval(interval);
    }
  }, [currentIndex, nextIndex]);

  // Flatten slides for mobile view (single page flow)
  /* Mobile States */
  const [mobileIndex, setMobileIndex] = useState(0);
  const [mobileDirection, setMobileDirection] = useState(1);
  const mobileSlides = slides.flatMap(slide => [
    { image: slide.leftImage, text: slide.leftText },
    { image: slide.rightImage, text: slide.rightText }
  ]);

  const mobileNextIndex = mobileIndex < mobileSlides.length - 1 ? mobileIndex + 1 : null;
  const mobilePrevIndex = mobileIndex > 0 ? mobileIndex - 1 : null;

  const handleMobileNext = () => {
    if (mobileNextIndex !== null) {
      setMobileDirection(1);
      setMobileIndex(mobileNextIndex);
    }
  };

  const handleMobilePrev = () => {
    if (mobilePrevIndex !== null) {
      setMobileDirection(-1);
      setMobileIndex(mobilePrevIndex);
    }
  };

  /* Auto-advance Mobile */
  useEffect(() => {
    if (mobileNextIndex !== null) {
      const interval = setInterval(() => {
        handleMobileNext();
      }, 6000);
      return () => clearInterval(interval);
    }
  }, [mobileIndex, mobileNextIndex]);


  return (
    <section className="py-20 bg-white relative overflow-hidden">
      <div
        className="max-w-7xl mx-auto px-4 perspective-container relative"
        style={{ perspective: '2500px' }}
      >

        {/* Mobile View (3D Single Page Flip) */}
        <div className="md:hidden relative h-[500px] w-full max-w-sm mx-auto shadow-2xl" style={{ transformStyle: 'preserve-3d' }}>

          {/* Mobile Navigation Buttons */}
          {mobilePrevIndex !== null && (
            <button
              onClick={handleMobilePrev}
              className="absolute left-[-15px] top-1/2 -translate-y-1/2 z-40 bg-white/90 p-2 rounded-full shadow-lg text-[#008000]"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}
          {mobileNextIndex !== null && (
            <button
              onClick={handleMobileNext}
              className="absolute right-[-15px] top-1/2 -translate-y-1/2 z-40 bg-white/90 p-2 rounded-full shadow-lg text-[#008000]"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}

          {/* Book Container Content */}
          <div className="absolute inset-0 w-full h-full">
            {/* Static Layer */}
            <div className="absolute inset-0 w-full h-full bg-white rounded-xl overflow-hidden border border-gray-100">
              <SinglePageContent
                item={mobileDirection === 1
                  ? mobileSlides[mobileIndex]
                  : (mobileNextIndex !== null ? mobileSlides[mobileNextIndex] : null)
                }
              />
            </div>

            {/* Flippable Layer */}
            <AnimatePresence mode="popLayout" custom={mobileDirection}>
              <motion.div
                key={mobileIndex}
                custom={mobileDirection}
                className="absolute inset-0 w-full h-full bg-white rounded-xl overflow-hidden border border-gray-100"
                style={{ transformOrigin: 'left center', transformStyle: 'preserve-3d', zIndex: 10, backfaceVisibility: 'hidden' }}
                initial={(custom) => ({ rotateY: custom === 1 ? 0 : -180 })}
                animate={(custom) => ({ rotateY: custom === 1 ? -180 : 0 })}
                exit={{ opacity: 0, transition: { duration: 0 } }}
                transition={{ duration: 0.8, ease: "easeInOut" }}
              >
                <SinglePageContent
                  item={
                    mobileDirection === 1
                      ? (mobilePrevIndex !== null ? mobileSlides[mobilePrevIndex] : null)
                      : mobileSlides[mobileIndex]
                  }
                />

                {/* Backface / Shadow for visual depth during flip */}
                <div className="absolute inset-0 bg-black/5 pointer-events-none" />
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="absolute -bottom-8 left-0 right-0 text-center text-sm text-gray-500">
            {mobileIndex + 1} / {mobileSlides.length}
          </div>
        </div>

        {/* Desktop View (3D Book) */}
        <div className="hidden md:block relative h-[600px] w-full max-w-6xl mx-auto shadow-2xl" style={{ transformStyle: 'preserve-3d' }}>

          {/* Navigation Buttons for Desktop */}
          {prevIndex !== null && (
            <button
              onClick={handlePrev}
              className="absolute left-[-60px] top-1/2 -translate-y-1/2 z-40 bg-white/90 p-3 rounded-full shadow-lg hover:bg-white transition-colors text-[#008000] hover:scale-110 duration-200"
              aria-label="Previous"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
          )}

          {nextIndex !== null && (
            <button
              onClick={handleNext}
              className="absolute right-[-60px] top-1/2 -translate-y-1/2 z-40 bg-white/90 p-3 rounded-full shadow-lg hover:bg-white transition-colors text-[#008000] hover:scale-110 duration-200"
              aria-label="Next"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          )}

          <div className="absolute inset-0 grid grid-cols-2">
            {/* Static Left */}
            <div
              className="h-full bg-gradient-to-br from-[#B8D8E8] to-[#A8C8D8] relative overflow-hidden rounded-l-xl border-r border-black/5"
            >
              {/* Logic: 
                   If Dir=1 (Next): Show Prev Slide (Wait, if I am at 0->1. Prev=0. Static Left should be 0).
                   If Dir=-1 (Prev): Show Current Slide (Wait, if I am at 1->0. Curr=0. Static Left should be 0).
                   So Static Left ALWAYS shows the Left Side of what is effectively the "New Previous" or "Current"?
                   
                   Let's stick to the Correct Logic derived:
                   Next (1): Old Left (prevIndex).
                   Prev (-1): New Left (currentIndex).
               */}
              <PageContent
                slide={direction === 1 ? (prevIndex !== null ? slides[prevIndex] : null) : slides[currentIndex]}
                side="left"
              />
            </div>

            {/* Static Right */}
            <div
              className="h-full bg-gradient-to-br from-gray-900 to-gray-800 relative overflow-hidden rounded-r-xl border-l border-black/5"
            >
              {/* Logic:
                   Next (1): New Right (currentIndex).
                   Prev (-1): Old Right (nextIndex). (Wait, if go back 1->0. Old Right was 1R).
               */}
              <PageContent
                slide={direction === 1 ? slides[currentIndex] : (nextIndex !== null ? slides[nextIndex] : null)}
                side="right"
              />
            </div>
          </div>

          {/* Flippable Page Layer */}
          <AnimatePresence mode="popLayout" custom={direction}>
            <motion.div
              key={currentIndex}
              custom={direction}
              className="absolute right-0 top-0 w-1/2 h-full"
              style={{ transformOrigin: 'left center', transformStyle: 'preserve-3d', zIndex: 10 }}
              initial={(custom) => ({ rotateY: custom === 1 ? 0 : -180 })}
              animate={(custom) => ({ rotateY: custom === 1 ? -180 : 0 })}
              // Instant exit to prevent flashing of old flippers
              exit={{ opacity: 0, transition: { duration: 0 } }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
            >
              {direction === 1 ? (
                /* Forward: Front=Old Right (prevIndex), Back=New Left (currentIndex) */
                prevIndex !== null && (
                  <FlipperContent
                    front={slides[prevIndex]}
                    back={slides[currentIndex]}
                    direction={1}
                  />
                )
              ) : (
                /* Backward: Front=Old Left (nextIndex? NO), Back=New Right (currentIndex)?? 
                   Wait. Backward Logic Review:
                   Lift visible Left page (old state's Left = nextIndex? NO).
                   We are at 1. Go to 0.
                   Old state was 1. Left Page was 1L.
                   Right Page was 1R.
                   We lift 1L.
                   So "Back" of flipper (visible at -180) must be 1L (nextIndex).
                   It lands as 0R.
                   So "Front" of flipper (visible at 0) must be 0R (currentIndex).
                   So Front=0(curr), Back=1(next). 
                */
                nextIndex !== null && (
                  <FlipperContent
                    front={slides[currentIndex]}
                    back={slides[nextIndex]}
                    direction={-1}
                  />
                )
              )}
            </motion.div>
          </AnimatePresence>

          {/* Spine Highlight */}
          <div className="absolute inset-y-0 left-1/2 w-4 -ml-2 bg-gradient-to-r from-black/5 via-black/20 to-black/5 z-30 pointer-events-none blur-sm rounded-full" />

        </div>

        {/* Badge */}
        {/* <div className="absolute top-8 right-8 z-30 pointer-events-none">
          <div className="bg-gradient-to-br from-[#4CAF50] to-[#008000] text-white px-6 py-4 rounded-lg shadow-2xl transform rotate-[-5deg]">
            <div className="text-center">
              <p className="text-xs font-semibold uppercase tracking-wider">LIMITED TIME</p>
              <p className="text-2xl font-bold">SPECIAL OFFER</p>
              <p className="text-3xl font-extrabold">10% OFF</p>
            </div>
          </div>
        </div> */}

      </div>
    </section>
  );
};

// Reusable parts
const FlipperContent = ({ front, back, direction }) => (
  <>
    {/* Front Face (Visible at 0deg) */}
    <div
      className="absolute inset-0 backface-hidden bg-gradient-to-br from-gray-900 to-gray-800 overflow-hidden rounded-r-xl border-l border-gray-600"
      style={{ backfaceVisibility: 'hidden' }}
    >
      <PageContent slide={front} side="right" />

      <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-black/20 to-transparent pointer-events-none" />
    </div>

    {/* Back Face (Visible at -180deg) */}
    <div
      className="absolute inset-0 backface-hidden bg-gradient-to-br from-[#B8D8E8] to-[#A8C8D8] overflow-hidden rounded-l-xl border-r border-blue-200"
      style={{
        backfaceVisibility: 'hidden',
        transform: 'rotateY(180deg)'
      }}
    >
      <PageContent slide={back} side="left" />
      <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-black/20 to-transparent pointer-events-none" />
    </div>
  </>
);

const PageContent = ({ slide, side }) => {
  if (!slide) return <div className="w-full h-full bg-gray-100" />;

  return (
    <div className="relative w-full h-full flex items-center justify-center p-8 lg:p-12 select-none">
      <div className="relative z-10 w-full h-full">
        <img
          src={side === 'left' ? slide.leftImage : slide.rightImage}
          alt={side}
          className={`w-full h-full object-cover shadow-xl ${side === 'left' ? 'opacity-60 rounded-lg' : 'rounded-lg'}`}
          draggable="false"
        />
        <div className="absolute inset-0 flex items-center justify-center text-center">
          <h2 className={`text-4xl md:text-5xl font-bold uppercase tracking-wider whitespace-pre-line drop-shadow-lg ${side === 'left' ? 'text-white' : 'text-white'}`}>
            {side === 'left' ? slide.leftText : slide.rightText}
          </h2>
        </div>
      </div>
    </div>
  );
};

const SinglePageContent = ({ item }) => {
  if (!item) return <div className="w-full h-full bg-gray-100" />;
  return (
    <div className="relative w-full h-full">
      <img
        src={item.image}
        alt="Page"
        className="w-full h-full object-cover"
        draggable="false"
      />
      <div className="absolute inset-0 bg-black/10 flex items-center justify-center">
        <h3 className="text-3xl font-bold text-white text-center whitespace-pre-line drop-shadow-lg px-4">
          {item.text}
        </h3>
      </div>
      {/* Paper Texture Overlay */}
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/paper-fibers.png')] opacity-30 pointer-events-none mix-blend-multiply" />
    </div>
  );
};

export default FoundationSection;